import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { api } from "@/api/client";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface AuthUser {
  user_id: string;
  username: string;
  roles: string[];
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  // Actions
  login: (credentials: { username: string; password: string }) => Promise<void>;
  logout: () => void;
  setAccessToken: (token: string) => void;
  refreshAccessToken: () => Promise<void>;
  hasRole: (role: string | string[]) => boolean;
}

// ── Store ─────────────────────────────────────────────────────────────────────
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      login: async (credentials) => {
        const response = await api.post<{
          access_token: string;
          refresh_token: string;
          user_id: string;
          username: string;
          roles: string[];
        }>("/auth/login", credentials);

        const user: AuthUser = {
          user_id: response.user_id,
          username: response.username,
          roles: response.roles,
        };

        set({
          user,
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
          isAuthenticated: true,
        });
      },

      logout: () => {
        // Call logout endpoint before clearing tokens
        api.post("/auth/logout", {
          refresh_token: get().refreshToken,
        }).catch(() => {}).finally(() => {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          });
        });
      },

      setAccessToken: (token: string) => {
        set({ accessToken: token });
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) throw new Error("No refresh token available");

        const response = await api.post<{ access_token: string }>("/auth/refresh", {
          refresh_token: refreshToken,
        });

        set({ accessToken: response.access_token });
      },

      hasRole: (role: string | string[]) => {
        const { user } = get();
        if (!user) return false;
        const roles = Array.isArray(role) ? role : [role];
        return roles.some((r) => user.roles.includes(r));
      },
    }),
    {
      name: "pnbp-auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
